import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { Entry } from '../types';

/**
 * Minimum drag span (width or height) to apply a marquee selection on pointer up.
 * Also used as the minimum on-screen size of the marquee so tiny drags do not jitter
 * (display rect is expanded around the drag center, independent of hit-testing).
 */
const MIN_MARQUEE_SELECT_PX = 4;

/** Stable rubber-band outline: expand short spans around the segment midpoint so left/top do not whip with sub-pixel noise. */
function getMarqueeDisplayRect(
	box: { x1: number; y1: number; x2: number; y2: number },
	minSpanPx: number,
): { left: number; top: number; width: number; height: number } {
	const minX = Math.min(box.x1, box.x2);
	const maxX = Math.max(box.x1, box.x2);
	const minY = Math.min(box.y1, box.y2);
	const maxY = Math.max(box.y1, box.y2);
	const rawW = maxX - minX;
	const rawH = maxY - minY;
	const cx = (minX + maxX) / 2;
	const cy = (minY + maxY) / 2;
	const width = Math.max(rawW, minSpanPx);
	const height = Math.max(rawH, minSpanPx);
	return {
		left: cx - width / 2,
		top: cy - height / 2,
		width,
		height,
	};
}

function rectsOverlapScreen(
	a: { left: number; top: number; right: number; bottom: number },
	b: { left: number; top: number; right: number; bottom: number },
): boolean {
	return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

export type MarqueeDisplayRect = {
	left: number;
	top: number;
	width: number;
	height: number;
};

type UseCanvasMarqueeSelectArgs = {
	containerRef: React.RefObject<HTMLDivElement | null>;
	visibleEntries: Entry[];
	cardNodeByIdRef: React.MutableRefObject<Map<string, HTMLDivElement>>;
	expandSelectedIdsWithSwimlaneChildren: (selectedIds: string[]) => string[];
	setSelectedEntryIds: React.Dispatch<React.SetStateAction<string[]>>;
	handleEntryClick: (entryId: string) => void;
};

type UseCanvasMarqueeSelectResult = {
	marqueeDisplayRect: MarqueeDisplayRect | null;
	isMarqueeActive: boolean;
	consumeMarqueeSuppressedClick: () => boolean;
	/**
	 * Starts a marquee from the current pointer position (capture, origin).
	 * Call only when the canvas view has decided this pointer should marquee (e.g. shift + primary on background).
	 * Returns false if the gesture could not start (e.g. missing container); caller may then fall through to pan/zoom.
	 */
	beginMarqueePointerDown: (e: React.PointerEvent) => boolean;
	/** Returns true while a marquee drag is in progress for this pointer (caller should not run pan/zoom pointer-move). */
	handleMarqueePointerMove: (e: React.PointerEvent) => boolean;
	/** Finalizes marquee if active; always safe to call before pan/zoom pointer-up. */
	handleMarqueePointerUp: (e: React.PointerEvent) => void;
};

export default function useCanvasMarqueeSelect({
	containerRef,
	visibleEntries,
	cardNodeByIdRef,
	expandSelectedIdsWithSwimlaneChildren,
	setSelectedEntryIds,
	handleEntryClick,
}: UseCanvasMarqueeSelectArgs): UseCanvasMarqueeSelectResult {
	const marqueeSessionRef = useRef<{ pointerId: number; originX: number; originY: number } | null>(null);
	const marqueeRectRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
	const [marqueeBox, setMarqueeBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
	const suppressNextCanvasClickRef = useRef(false);

	const clientPointToContainerOffset = useCallback((clientX: number, clientY: number) => {
		const el = containerRef.current;
		if (!el) return null;
		const r = el.getBoundingClientRect();
		return { x: clientX - r.left, y: clientY - r.top };
	}, [containerRef]);

	const resetMarquee = useCallback(() => {
		marqueeSessionRef.current = null;
		marqueeRectRef.current = null;
		setMarqueeBox(null);
	}, []);

	const beginMarqueePointerDown = useCallback(
		(e: React.PointerEvent) => {
			const off = clientPointToContainerOffset(e.clientX, e.clientY);
			if (!off) return false;
			containerRef.current?.setPointerCapture?.(e.pointerId);
			marqueeSessionRef.current = { pointerId: e.pointerId, originX: off.x, originY: off.y };
			const r = { x1: off.x, y1: off.y, x2: off.x, y2: off.y };
			marqueeRectRef.current = r;
			setMarqueeBox(r);
			return true;
		},
		[clientPointToContainerOffset, containerRef],
	);

	const handleMarqueePointerMove = useCallback(
		(e: React.PointerEvent) => {
			const session = marqueeSessionRef.current;
			if (session && e.pointerId === session.pointerId) {
				const off = clientPointToContainerOffset(e.clientX, e.clientY);
				if (!off) return true;
				const r = {
					x1: session.originX,
					y1: session.originY,
					x2: off.x,
					y2: off.y,
				};
				marqueeRectRef.current = r;
				setMarqueeBox(r);
				return true;
			}
			return false;
		},
		[clientPointToContainerOffset],
	);

	const handleMarqueePointerUp = useCallback(
		(e: React.PointerEvent) => {
			const session = marqueeSessionRef.current;
			if (session && e.pointerId === session.pointerId) {
				suppressNextCanvasClickRef.current = true;
				const rect = marqueeRectRef.current;
				const containerEl = containerRef.current;
				if (rect && containerEl) {
					const dx = Math.abs(rect.x2 - rect.x1);
					const dy = Math.abs(rect.y2 - rect.y1);
					if (dx >= MIN_MARQUEE_SELECT_PX || dy >= MIN_MARQUEE_SELECT_PX) {
						const cr = containerEl.getBoundingClientRect();
						const sel = {
							left: cr.left + Math.min(rect.x1, rect.x2),
							top: cr.top + Math.min(rect.y1, rect.y2),
							right: cr.left + Math.max(rect.x1, rect.x2),
							bottom: cr.top + Math.max(rect.y1, rect.y2),
						};
						const hitIds: string[] = [];
						for (const entry of visibleEntries) {
							const node = cardNodeByIdRef.current.get(entry.id);
							if (!node) continue;
							const br = node.getBoundingClientRect();
							if (rectsOverlapScreen(sel, br)) {
								hitIds.push(entry.id);
							}
						}
						if (hitIds.length) {
							const expanded = expandSelectedIdsWithSwimlaneChildren(hitIds);
							setSelectedEntryIds((prev) => Array.from(new Set([...prev, ...expanded])));
							const expandedSet = new Set(expanded);
							let primaryId: string | null = null;
							for (let i = visibleEntries.length - 1; i >= 0; i--) {
								const id = visibleEntries[i].id;
								if (expandedSet.has(id)) {
									primaryId = id;
									break;
								}
							}
							if (primaryId) {
								handleEntryClick(primaryId);
							}
						}
					}
				}
				resetMarquee();
			}
		},
		[
			cardNodeByIdRef,
			containerRef,
			expandSelectedIdsWithSwimlaneChildren,
			handleEntryClick,
			resetMarquee,
			setSelectedEntryIds,
			visibleEntries,
		],
	);

	const marqueeDisplayRect = useMemo(
		() => (marqueeBox ? getMarqueeDisplayRect(marqueeBox, MIN_MARQUEE_SELECT_PX) : null),
		[marqueeBox],
	);

	const consumeMarqueeSuppressedClick = useCallback(() => {
		if (!suppressNextCanvasClickRef.current) return false;
		suppressNextCanvasClickRef.current = false;
		return true;
	}, []);

	return {
		marqueeDisplayRect,
		isMarqueeActive: Boolean(marqueeBox),
		consumeMarqueeSuppressedClick,
		beginMarqueePointerDown,
		handleMarqueePointerMove,
		handleMarqueePointerUp,
	};
}
